<script setup lang="ts">
import { faCloudArrowUp, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faFileCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { type WebAppFileType } from '@composable/useUploadFile';

export type UploadFile = { id: number; file: File; size: string };
const {
    dataTypes,
    maxFiles,
    maxFileSize,
    webAppFileType,
    webAppFileExtensions,
} = defineProps<{
    dataTypes?: string[];
    maxFiles?: number;
    label?: string;
    maxFileSize?: number;
    webAppFileType?: WebAppFileType;
    webAppFileExtensions?: string[];
    noFileLabel?: boolean;
}>();
const modelValue = defineModel<UploadFile[] | null>();

const { dropZoneRef, files, openFileSelect, reachedMax, removeFile } =
    useDropzone({
        dataTypes,
        maxNumberOfFiles: maxFiles || 1,
        maxFileSize,
        webAppFileType,
        webAppFileExtensions,
    });

watch(
    modelValue,
    value => {
        if (value) {
            files.value = value;
        }
    },
    { deep: true }
);
watch(
    files,
    value => {
        modelValue.value = value;
    },
    { deep: true }
);
</script>
<template>
    <div class="upload-root">
        <div ref="dropZoneRef" class="upload-dropzone">
            <div class="upload-icon-outer-wrapper">
                <div class="upload-icon-middle-wrapper">
                    <div class="upload-icon-inner-wrapper">
                        <mr-icon
                            :icon="faCloudArrowUp"
                            size="x-large"
                            color="var(--blue-400)"
                        />
                    </div>
                </div>
            </div>
            <mr-button
                class="clickable"
                variant="bordered"
                :disabled="reachedMax"
                @click="() => openFileSelect()"
            >
                انتخاب {{ noFileLabel ? '' : 'فایل' }} {{ label }}...
            </mr-button>
            <span class="description">
                یا {{ noFileLabel ? '' : 'فایل' }} {{ label }} خود را به اینجا
                بکشید و رها کنید.
            </span>
        </div>
        <div v-for="(file, index) in files" :key="index" class="file-preview">
            <mr-icon
                size="x-large"
                :icon="faFileCircleCheck"
                color="var(--cool-gray-600)"
            />
            <span class="file-name">
                {{ file.file.name }} ({{ file.size }})
            </span>
            <mr-button
                size="medium"
                variant="transparent"
                :right-icon="faTrashCan"
                class="desktop"
                @click="removeFile(file.id)"
            >
                حذف
            </mr-button>
            <mr-button
                size="medium"
                variant="transparent"
                :icon-button="faTrashCan"
                class="mobile"
                @click="removeFile(file.id)"
            />
        </div>
    </div>
</template>
<style scoped lang="scss">
.upload-root {
    display: flex;
    flex-direction: column;
    gap: 20px;
    .upload-dropzone {
        border-radius: 12px;
        border: 1px dashed usePalette();
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 24px;
        gap: 24px;
        @include mobile-only {
            border: none;
        }

        .upload-icon-outer-wrapper {
            width: 80px;
            height: 80px;
            border-radius: 100%;
            background-color: usePalette($p-blue, $p-100);
            display: flex;
            align-items: center;
            justify-content: center;

            .upload-icon-middle-wrapper {
                width: 77px;
                height: 77px;
                border-radius: 100%;
                background-color: white;
                display: flex;
                align-items: center;
                justify-content: center;

                .upload-icon-inner-wrapper {
                    width: 64px;
                    height: 64px;
                    border-radius: 100%;
                    background-color: usePalette($p-blue, $p-100);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }
        }

        .clickable {
            min-width: 200px;
            white-space: nowrap;
            @include mobile-only {
                width: 150px;
            }
        }

        .description {
            @include tg-l;
            @include mobile-only {
                display: none;
            }
        }
    }
    .file-preview {
        display: flex;
        align-items: center;
        padding: 12px;
        gap: 8px;
        border-radius: 12px;
        border: 1px solid usePalette($p-cool-gray, $p-200);
        @include mobile-only {
            padding: 8px;
        }
        .file-name {
            @include tg-l;
            flex-grow: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .desktop {
            @include mobile-only {
                display: none;
            }
        }
        .mobile {
            @include desktop-only {
                display: none;
            }
        }
    }
    .submit {
        width: 200px;
        align-self: flex-end;
        @include mobile-only {
            width: 100%;
        }
    }
}
</style>
